import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { FaStar, FaCheck, FaTimes, FaTrash, FaSearch, FaClock, FaCheckCircle, FaTimesCircle, FaThumbsUp, FaThumbsDown, FaMapMarkerAlt, FaQuoteLeft } from 'react-icons/fa';
import AdminSidebar from '../components/AdminSidebar';

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) navigate('/admin/login');
  }, [navigate]);

  const fetchReviews = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    fetchReviews();
  }, [checkAuth, fetchReviews]);

  const updateStatus = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from('reviews')
        .update({ status: newStatus })
        .eq('id', id);
      if (error) throw error;
      fetchReviews();
    } catch (error) {
      console.error('Error updating review:', error);
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta reseña?')) return;
    try {
      const { error } = await supabase.from('reviews').delete().eq('id', id);
      if (error) throw error;
      fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
    }
  };

  const filteredReviews = reviews.filter(r => {
    const matchesFilter = filter === 'all' || r.status === filter;
    const matchesSearch = searchTerm === '' ||
      r.reviewer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.comment.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const counts = {
    all: reviews.length,
    pending: reviews.filter(r => r.status === 'pending').length,
    approved: reviews.filter(r => r.status === 'approved').length,
    rejected: reviews.filter(r => r.status === 'rejected').length,
  };

  const renderStars = (rating) => (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <FaStar key={i} size={14} className={i < rating ? 'text-yellow-400' : 'text-gray-300'} />
      ))}
    </div>
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-PE', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: 'bg-yellow-100 text-yellow-800', icon: <FaClock className="inline mr-1" size={10} />, label: 'Pendiente' },
      approved: { bg: 'bg-green-100 text-green-800', icon: <FaCheckCircle className="inline mr-1" size={10} />, label: 'Aprobada' },
      rejected: { bg: 'bg-red-100 text-red-800', icon: <FaTimesCircle className="inline mr-1" size={10} />, label: 'Rechazada' },
    };
    const c = config[status] || config.pending;
    return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${c.bg}`}>{c.icon}{c.label}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      <AdminSidebar />

      <div className="flex-1 w-full md:w-auto">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8 py-4 md:py-6">
            <h1 className="text-xl md:text-2xl lg:text-3xl font-playfair font-bold text-gray-900">
              Gestión de Reseñas
            </h1>
            <p className="text-xs md:text-sm text-gray-600 mt-1">Modera y responde las reseñas de huéspedes</p>
          </div>
        </div>

        <div className="px-4 sm:px-6 lg:px-8 py-4 md:py-6">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3 lg:gap-4 mb-4 md:mb-6">
            <div className="bg-white p-3 md:p-4 rounded-lg shadow text-center">
              <p className="text-2xl font-bold text-gray-900">{counts.all}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div className="bg-white p-3 md:p-4 rounded-lg shadow text-center">
              <p className="text-2xl font-bold text-yellow-600">{counts.pending}</p>
              <p className="text-xs text-gray-500">Pendientes</p>
            </div>
            <div className="bg-white p-3 md:p-4 rounded-lg shadow text-center">
              <p className="text-2xl font-bold text-green-600">{counts.approved}</p>
              <p className="text-xs text-gray-500">Aprobadas</p>
            </div>
            <div className="bg-white p-3 md:p-4 rounded-lg shadow text-center">
              <p className="text-2xl font-bold text-red-600">{counts.rejected}</p>
              <p className="text-xs text-gray-500">Rechazadas</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3 mb-4 md:mb-6">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {[
                { key: 'pending', label: 'Pendientes', color: 'yellow' },
                { key: 'approved', label: 'Aprobadas', color: 'green' },
                { key: 'rejected', label: 'Rechazadas', color: 'red' },
                { key: 'all', label: 'Todas', color: 'gray' },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition whitespace-nowrap text-xs md:text-sm ${
                    filter === f.key
                      ? f.key === 'pending' ? 'bg-yellow-500 text-white'
                        : f.key === 'approved' ? 'bg-green-600 text-white'
                        : f.key === 'rejected' ? 'bg-red-500 text-white'
                        : 'bg-gray-700 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {f.label} ({counts[f.key]})
                </button>
              ))}
            </div>

            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Buscar por nombre o comentario..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          {/* Reviews List */}
          <div className="space-y-4">
            {filteredReviews.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <FaQuoteLeft className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-gray-500 text-lg">No hay reseñas con este filtro.</p>
              </div>
            ) : (
              filteredReviews.map((review) => (
                <div key={review.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-green-50 to-yellow-50 px-4 md:px-6 py-3 md:py-4 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-green-700 font-bold text-lg">
                            {review.reviewer_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-base md:text-lg font-bold text-gray-900">{review.reviewer_name}</h3>
                          {review.reviewer_city && (
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <FaMapMarkerAlt size={10} /> {review.reviewer_city}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {review.liked ? (
                          <FaThumbsUp className="text-green-500" size={14} />
                        ) : (
                          <FaThumbsDown className="text-red-400" size={14} />
                        )}
                        {getStatusBadge(review.status)}
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 md:p-6">
                    <div className="mb-3">
                      {renderStars(review.rating)}
                    </div>

                    <p className="text-gray-700 text-sm leading-relaxed mb-3">{review.comment}</p>

                    {/* Photos */}
                    {review.photo_urls && review.photo_urls.length > 0 && (
                      <div className="flex gap-2 mb-3">
                        {review.photo_urls.map((url, i) => (
                          <button
                            key={i}
                            onClick={() => setSelectedPhoto(url)}
                            className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 hover:opacity-80 transition-opacity flex-shrink-0"
                          >
                            <img src={url} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}

                    <p className="text-xs text-gray-400 mb-3">{formatDate(review.created_at)}</p>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
                      {review.status === 'pending' && (
                        <>
                          <button
                            onClick={() => updateStatus(review.id, 'approved')}
                            className="flex items-center gap-1.5 bg-green-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-green-600 transition"
                          >
                            <FaCheck size={10} /> Aprobar
                          </button>
                          <button
                            onClick={() => updateStatus(review.id, 'rejected')}
                            className="flex items-center gap-1.5 bg-red-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-red-600 transition"
                          >
                            <FaTimes size={10} /> Rechazar
                          </button>
                        </>
                      )}
                      {review.status === 'rejected' && (
                        <button
                          onClick={() => updateStatus(review.id, 'approved')}
                          className="flex items-center gap-1.5 bg-green-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-green-600 transition"
                        >
                          <FaCheck size={10} /> Aprobar
                        </button>
                      )}
                      {review.status === 'approved' && (
                        <button
                          onClick={() => updateStatus(review.id, 'rejected')}
                          className="flex items-center gap-1.5 bg-yellow-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-yellow-600 transition"
                        >
                          <FaTimes size={10} /> Ocultar
                        </button>
                      )}
                      <button
                        onClick={() => deleteReview(review.id)}
                        className="flex items-center gap-1.5 bg-gray-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-600 transition"
                      >
                        <FaTrash size={10} /> Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Photo Lightbox */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <img src={selectedPhoto} alt="Foto de reseña" className="max-w-full max-h-[90vh] rounded-lg object-contain" />
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
